import { Injectable } from '@angular/core'
import { format, isSameDay } from 'date-fns'
import orderBy from 'lodash-es/orderBy'
import { combineLatest, Observable } from 'rxjs'
import { first, map } from 'rxjs/operators'
import { SubSink } from 'subsink'
import { ApiService } from './api.service'
import { EmailService } from './email.service'
import { PrimaryCode, TransactionCode } from './enums'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { ChallengeAnswer, ChallengeTransaction, GameBase } from './interfaces'
import { TimestampsService } from './timestamp.service'
import { getServerTime } from './timestampUtils'
import { UserService } from './user.service'
import { timeOut, use } from './utils'
import { XlsService } from './xls.service'

@Injectable({
  providedIn: 'root'
})
export class ChallengesService {

  private subs = new SubSink()
  private fss: FirestoreService2

  constructor(
    private xlsService: XlsService,
    private g: GlobalService,
    private userService: UserService,
  ) { }

  /**
   * Creates an Excel file with challenge results by user participation
   */
  exportParticipationSegmentByChallenge() {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)
    this.subs.add(
      combineLatest([
        this.fss.list('settings', { subcol: 'games', where: [['active', '==', true]] }),
        api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'users' }, true),
        this.fss.list('challenges', { where: [['code', 'in', ['find-the-pair', 'quiz', 'captcha']]] }),
        this.fss.list('segments', { where: [['active', '==', true]] }),
      ]).subscribe((results: any[]) => {
        const games = results[0]
        const users = results[1]
        const challenges = results[2]
        const segments = results[3]
        const res = {}
        const table = []

        for (const ch of challenges.filter(c => c.email)) {
          ch.segment = (users.filter(u => u.email).find(u => u.email.toLowerCase().trim() === ch.email.toLowerCase().trim()) || { segment: null }).segment
        }

        this.subs.unsubscribe()

        for (const game of games) {
          if (!res.hasOwnProperty(game.name)) res[game.name] = {}
          for (const ch of challenges.filter(c => c.challengeId === game.id)) {
            if (!res[game.name].hasOwnProperty(ch.segment)) res[game.name][ch.segment] = 1
            else res[game.name][ch.segment]++
          }
        }

        for (const seg of orderBy(segments, 'name')) {
          const line = { Equipa: seg.name }
          for (const game in res) {
            line[game] = res[game][seg.name] || 0
          }
          table.push(line)
        }

        console.log('TABLE', table)
        this.xlsService.export('games.xlsx', table)
      })
    )
  }

  /**
   * Creates a new game in settings/NICK/games
   * @param {GameBase} base
   * @param {any} game
   * @returns {Promise<any>}
   */
  createGame(base: GameBase, game: any): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    // console.log(base, game);
    const data = { ...game, ...base }
    return this.fss.merge('settings', data.id || this.fss.createId, data, { subcol: 'games' })
  }

  /**
   * Applies points to a user
   * @param {any} user
   * @param {ChallengeTransaction} transaction
   * @returns {Promise<any>}
   */
  applyPointsToUser(user: any, transaction: ChallengeTransaction, timeToFinish = null): Promise<any> {
    console.log('# applyPointsToUser')
    const data = {
      ...transaction,
      active: true,
      approved: true,
      code: TransactionCode.ADD_POINTS,
      collection: 'transactions',
      displayName: user.displayName,
      email: user.email.toLowerCase(),
      firstName: user.firstName || '',
      group: user.group || '',
      name: user.name || user.displayName || '',
      nick: this.g.nick,
      orderDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
      primaryCode: PrimaryCode.POINTS,
      segment: user.segment || null,
      timeToFinish: timeToFinish,
      uid: user.uid || user.id || undefined,
      userCode: user.userCode || null,
      xrate: this.g.program.xrate || 1,
    }
    const api = use<ApiService>(ApiService)
    return api.save('Points', 'transactions', [data], 'id')
    // .then(() => this.userService.recalcBalance({
    //   userCode: user.userCode || null,
    //   email: user.email.toLowerCase(),
    //   displayName: user.displayName,
    //   id: user.id,
    //   nick: this.g.nick
    // }))
    // .then(() => this.sendEmailOnFinish(transaction.challengeId, user))
  }

  /**
   * Applies points to a segment
   * @param {any} user
   * @param {ChallengeTransaction} transaction
   * @returns {Promise<any>}
   */
  applyPointsToSegment(user: any, transaction: ChallengeTransaction, timeToFinish = null): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    console.log('# applyPointsToSegment')
    const data = {
      ...transaction,
      active: true,
      email: user ? (user.email || '').toLowerCase() : '',
      code: TransactionCode.ADD_POINTS,
      group: user.group || '',
      primaryCode: PrimaryCode.POINTS,
      displayName: user.displayName,
      nick: this.g.nick,
      orderDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
      timeToFinish: timeToFinish,
      xRate: this.g.program.xrate || 1,
    }
    const api = use<ApiService>(ApiService)
    return this.fss.add('transactions', data)
      .then(() => api.updateSegmentBalance({ nick: this.g.nick, segmentId: data.segment.id }))
    // .then(() => this.sendEmailOnFinish(transaction.challengeId, user))
  }

  private sendEmailOnFinish(challengeId: string, user: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    this.fss.get('settings', challengeId, { subcol: 'games', api: true }).subscribe(challenge => {
      if (challenge && challenge.emailTemplateOnFinish) {
        this.fss.get('templates', challenge.emailTemplateOnFinish, { api: true }).subscribe(template => {
          if (template) {
            const emailSrv: EmailService = use<EmailService>(EmailService)
            emailSrv.sendOneTemplate(user, template).subscribe()
          }
        })
      }
    })
  }

  /**
   * Saves a participation in a game/challenge according to interface standards
   * @param {any} user
   * @param {ChallengeAnswer} base
   * @param {any} challenge
   * @returns {Promise<any>}
   */
  saveChallengeAnswer(base: ChallengeAnswer, challenge: any, timeToFinish = null, applyPoints: boolean | number = true, timeMultiplier: number | null = null, challengeConfig: any = null): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const timestamps = use<TimestampsService>(TimestampsService)
    console.log('# saveChallengeAnswer')
    const data = {
      ...challenge,
      ...base,
      email: base.email && base.email.toLowerCase(),
      serverDate: timestamps.getNewTimestamp()
    }
    return this.fss.add('challenges', data).then(() => !data.public && !!applyPoints && this.applyPointsForChallenge(data, timeToFinish, null, timeMultiplier, challengeConfig))
  }

  /**
   * Saves a participation in a game/challenge with a defined id according to interface standards
   * @param {any} user
   * @param {ChallengeAnswer} base
   * @param {any} challenge
   * @returns {Promise<any>}
   */
  setChallengeAnswer(base: ChallengeAnswer, challenge: any, timeToFinish = null, applyPoints: boolean | number = true, timeMultiplier: number | null = null, challengeConfig: any = null): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const timestamps = use<TimestampsService>(TimestampsService)
    console.log('# setChallengeAnswer')
    const data = { ...challenge, ...base, email: base.email && base.email.toLowerCase(), serverDate: timestamps.getNewTimestamp() }

    if (!data.id) data.id = this.fss.createId

    return this.fss.set('challenges', data.id, data).then(() => !data.public && !!applyPoints && this.applyPointsForChallenge(data, timeToFinish, null, timeMultiplier, challengeConfig))
  }

  /**
   * Only checks if there are extra points to assign
   * Useful for displaying badges on screens
   * @param challenge
   * @param answer
   * @returns {any[]}
   */
  verifyExtraPoints(challenge: any, answer: any, preview = false): any[] {
    // console.log('# verifyExtraPoints');
    const points: any[] = []
    if (challenge.scriptPointsConfig) {
      for (const functionType in challenge.scriptPointsConfig) {
        if (!preview) {
          switch (functionType) {
            case 'forParticipating': points.push(challenge.scriptPointsConfig[functionType]); break
            case 'bySegmentParticipation': points.push(challenge.scriptPointsConfig[functionType]); break
            case 'hitAll':
              if (answer.resultQuiz && answer.resultQuiz.filter(r => r === true).length === challenge.questions.length) {
                points.push(challenge.scriptPointsConfig[functionType])
              }
              break
          }
        } else {
          if (challenge.scriptPointsConfig[functionType].points) points.push(challenge.scriptPointsConfig[functionType])
        }

      }
    }
    return preview ? points.filter(p => p.code.toLowerCase().indexOf('segment') === -1) : points
  }

  /**
   * Applies extra score when finishing a challenge
   * @param {Partial<ChallengeAnswer>} answer
   * @param challengeConfig Challenge object already loaded — avoids redundant GETs to Firestore
   */
  applyPointsForChallenge(answer: Partial<ChallengeAnswer>, timeToFinish, user: any = null, timeMultiplier: number | null = null, challengeConfig: any = null) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    console.log('# applyPointsForChallenge')
    if (this.g.program && answer && answer.challengeId) {
      if (challengeConfig) {
        const applyUser = user || this.g.user || null
        this.processPointsForChallenge(challengeConfig, answer, applyUser, timeToFinish, timeMultiplier)
      } else {
        this.subs.add(
          combineLatest([
            this.fss.get('settings', answer.challengeId, { subcol: 'games', api: true }),
            this.fss.get('settings', answer.challengeId, { subcol: 'challenges', api: true }),
            this.fss.get('settings', answer.challengeId, { subcol: 'questionnaires', api: true }),
          ]).pipe(first(), map((results: any[]) => results[0] || results[1] || results[2] || null)).subscribe((challenge: any) => {
            const applyUser = user || this.g.user || null
            this.processPointsForChallenge(challenge, answer, applyUser, timeToFinish, timeMultiplier)
          })
        )
      }
      timeOut(() => this.sendEmailOnFinish(answer.challengeId, user || this.g.user), 2500)
    }
  }

  private processPointsForChallenge(challenge: any, answer: Partial<ChallengeAnswer>, applyUser: any, timeToFinish: any, timeMultiplier: number | null) {
    if (applyUser && challenge && challenge.scriptPointsConfig) {
      for (const functionType in challenge.scriptPointsConfig) {
        switch (functionType) {
          case 'forParticipating': this.forParticipating(challenge, answer, applyUser, this.g.program, timeToFinish, timeMultiplier); break
          case 'bySegmentParticipation': this.bySegmentParticipation(challenge, answer, applyUser, this.g.program, timeToFinish, timeMultiplier); break
          case 'hitAll': this.hitAll(challenge, answer, applyUser, this.g.program, timeToFinish, timeMultiplier); break
        }
      }
    }
  }

  /**
   * Awards points to the user for participating in a challenge
   * @param challenge
   * @param answer
   * @param user
   * @param program
   * @param timeMultiplier Time multiplier (training context), or null
   */
  private forParticipating(challenge: any, answer: any, user: any, program: any, timeToFinish, timeMultiplier: number | null = null) {
    console.log('# forParticipating')
    const basePoints = challenge.scriptPointsConfig.forParticipating.points
    const awardedPoints = timeMultiplier !== null ? Math.round(basePoints * timeMultiplier) : basePoints

    const baseTr: any = {
      email: user.email.toLowerCase(),
      challengeId: answer.challengeId,
      tag: answer.tag || null,
      type: 'GAME',
      segment: user.segment || null,
      description: `[${challenge.name}] ${challenge.scriptPointsConfig.forParticipating.description}`,
      orderPoints: awardedPoints,
      forParticipating: true
    }
    this.applyPointsToUser(user, baseTr, timeToFinish).then().catch()
  }

  /**
   * Assigns points to team for each member participation
   * @param challenge
   * @param answer
   * @param user
   * @param program
   */
  private bySegmentParticipation(challenge: any, answer: any, user: any, program: any, timeToFinish, timeMultiplier: number | null = null) {
    console.log('# bySegmentParticipation')
    if (user.segment) {
      const basePoints = challenge.scriptPointsConfig.bySegmentParticipation.points
      const awardedPoints = timeMultiplier !== null ? Math.round(basePoints * timeMultiplier) : basePoints

      const baseTr: any = {
        segment: user.segment || null,
        tag: answer.tag || null,
        type: 'segments',
        orderPoints: awardedPoints,
        challengeId: answer.challengeId,
        description: `[${challenge.name}] ${challenge.scriptPointsConfig.bySegmentParticipation.description}`,
        bySegmentParticipation: true
      }
      this.applyPointsToSegment(user, baseTr, timeToFinish).then().catch()
    }
  }

  /**
   * Awards points for getting all quiz answers right (100%)
   * @param challenge
   * @param answer
   * @param user
   * @param program
   */
  private hitAll(challenge: any, answer: any, user: any, program: any, timeToFinish, timeMultiplier: number | null = null) {
    console.log('# hitAll')
    const basePoints = challenge.scriptPointsConfig.hitAll.points
    const awardedPoints = timeMultiplier !== null ? Math.round(basePoints * timeMultiplier) : basePoints

    const perguntas = challenge.questions ? challenge.questions.length : answer.result ? challenge.fields.filter(f => f.name !== 'html').length : 0
    // console.log('#perguntas', perguntas)
    if (answer.resultQuiz && answer.resultQuiz.filter(r => r === true).length === perguntas) {
      const baseTr: any = {
        email: user.email.toLowerCase(),
        orderPoints: awardedPoints,
        segment: user.segment || null,
        description: `[${challenge.name}] ${challenge.scriptPointsConfig.hitAll.description}`,
        tag: answer.tag || null,
        type: 'GAME',
        challengeId: answer.challengeId,
        hitAll: true
      }
      this.applyPointsToUser(user, baseTr, timeToFinish).then().catch()
    }
    if (answer.result) {
      if (answer.result.length >= challenge.fields.filter(f => f.name !== 'html').length) {
        const baseTr: any = {
          email: user.email.toLowerCase(),
          orderPoints: awardedPoints,
          segment: user.segment || null,
          description: `[${challenge.name}] ${challenge.scriptPointsConfig.hitAll.description}`,
          type: 'FORM',
          challengeId: answer.challengeId,
          formId: answer.challengeId,
          hitAll: true
        }
        this.applyPointsToUser(user, baseTr, timeToFinish).then().catch()
      }
    }
  }

  /**
   * Awards points to the user for voting in a challenge
   * @param challenge
   * @param answer
   * @param user
   * @param program
   */
  forVoting(challenge: any, answer: any, user: any, program: any) {
    console.log('# forParticipating')
    const baseTr: any = {
      email: user.email.toLowerCase(),
      challengeId: answer.challengeId,
      tag: answer.tag || null,
      type: 'GAME',
      segment: user.segment || null,
      description: `[${challenge.name}] ${challenge.votingParams.points} pontos por votar`,
      orderPoints: challenge.votingParams.points,
      forVoting: true
    }
    this.applyPointsToUser(user, baseTr).then().catch()
  }

  clearAllParticipations(id: string) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)
    combineLatest([
      this.fss.list('challenges', { api: true, where: [['challengeId', '==', id]] }),
      api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'transactions', api: true, queryArray: [['challengeId', '==', id]] }, true),
    ]).subscribe((results: any[]) => {
      if (results[0]) this.fss.deleteBatch('challenges', results[0]).then()
      if (results[1]) this.fss.deleteBatch('transactions', results[1]).then()
    })
  }

  getGalleryFiles(config): any {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Promise(resolve => {
      this.subs.add(
        this.fss.list(`settings/${this.g.program.nick}/challenges/${config.id}/files`, { rawPath: true, api: true }).subscribe(files => {
          if (files && files.length) {
            const rawFiles = files
            const votes = rawFiles.filter(o => o.votes && o.votes.includes(this.g.user.email.toLowerCase())).length
            // console.log({ votes: votes, files: rawFiles })
            resolve({ votes: votes, files: rawFiles })
          }
        })
      )
    })
  }

  getGalleryFilesFromChallenge(config, challengeId): any {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Promise(resolve => {
      this.subs.add(
        this.fss.list('challenges', { where: [['active', '==', true], ['challengeId', '==', challengeId], ['type', '==', 'CHALLENGE']], api: true }).subscribe(challenges => {
          if (challenges && challenges.length) {
            // console.log(challenges)
            const rawFiles = []

            for (const challenge of challenges) {
              if (challenge.answer) {
                rawFiles.push({ ...challenge.answer, email: challenge.email, id: challenge.id, folder: !challenge.answer.type || challenge.answer.type === 'image-upload' ? 'Fotos' : 'Frases', votes: challenge.votedBy })
              }
            }

            const votes = challenges.filter(o => o.votedBy && o.votedBy.includes(this.g.user.email.toLowerCase())).length
            // console.log({ votes: votes, files: rawFiles })
            resolve({ votes: votes, files: rawFiles })
          } else { resolve({ votes: 0, files: [] }) }
        })
      )
    })
  }

  getGalleryFilesFromChallengeObs(config, challengeId): Observable<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.list('challenges', { where: [['active', '==', true], ['challengeId', '==', challengeId], ['type', '==', 'CHALLENGE']] }).pipe(map(challenges => {
      if (challenges && challenges.length) {
        const rawFiles = []

        for (const challenge of challenges) {
          if (challenge.answer) {
            rawFiles.push({ ...challenge.answer, email: challenge.email, id: challenge.id, folder: !challenge.answer.type || challenge.answer.type === 'image-upload' ? 'Fotos' : 'Frases', votes: challenge.votedBy })
          }
        }

        const votes = challenges.filter(o => o.votedBy && o.votedBy.includes(this.g.user.email.toLowerCase())).length
        return { votes: votes, files: rawFiles }
      } else {
        return { votes: 0, files: [] }
      }
    }))
  }

  /**
   * Calculates the time multiplier for training challenges
   * @param timeToFinish Elapsed time in seconds
   * @param maxTimeSeconds Maximum time for the 1x multiplier
   * @returns Multiplier between 1 and 3
   */
  private calculateTimeMultiplier(timeToFinish: number, maxTimeSeconds: number, maxMultiplier: number): number {
    const minMultiplier = 1
    // Formula: multiplier = maxMultiplier - (elapsedSeconds / maxTimeSeconds) * (maxMultiplier - minMultiplier)
    const multiplier = maxMultiplier - (timeToFinish / maxTimeSeconds) * (maxMultiplier - minMultiplier)
    // Clamp between minMultiplier and maxMultiplier
    return Math.max(minMultiplier, Math.min(maxMultiplier, multiplier))
  }

  async saveGame(config: any, timeToFinish: number): Promise<any> {
    // Read sessionStorage context before any await to avoid race conditions
    const challengeUserEmail = sessionStorage.getItem('challengeUserEmail')
    const userEmail = challengeUserEmail || this.g.user?.email || ''

    const challengeUserClientStr = sessionStorage.getItem('challengeUserClient')
    let challengeUserClient: any = null
    try {
      challengeUserClient = challengeUserClientStr ? JSON.parse(challengeUserClientStr) : null
    } catch { challengeUserClient = null }

    const formationChallengeStr = sessionStorage.getItem('formationChallenge')
    let formationChallenge: any = null
    try {
      formationChallenge = formationChallengeStr ? JSON.parse(formationChallengeStr) : null
    } catch { formationChallenge = null }
    const formationId = formationChallenge?.formationId || null

    // Calculate time multiplier if there is a training context
    let timeMultiplier: number | null = null
    let effectiveMaxTimeSeconds: number | null = null
    if (formationChallenge) {
      if (config.game.timeToGameOver) {
        effectiveMaxTimeSeconds = config.game.timeToGameOver * 60
      } else {
        effectiveMaxTimeSeconds = formationChallenge.maxTimeSeconds || 300
      }
      timeMultiplier = this.calculateTimeMultiplier(timeToFinish, effectiveMaxTimeSeconds, formationChallenge.multiplier ?? 1.2)
    }

    try {
      const gameResult: any = {
        ...config.game,
        checkAllAnswers: config.game.checkAllAnswers ?? null,
        correctAnswers: config.game.correctAnswers || 0,
        resultQuiz: config.resultQuiz || null,
        timeToFinish: timeToFinish,
        uploads: config.game.uploads || null,
      }
      delete gameResult.id
      delete gameResult.createdAt
      delete gameResult.userGames

      if (timeMultiplier !== null) {
        gameResult.timeMultiplier = timeMultiplier
        gameResult.effectiveMaxTimeSeconds = effectiveMaxTimeSeconds
      }

      const answerBase: ChallengeAnswer = {
        challengeId: config.game.id,
        challengeWon: config.game.challengeWon,
        code: config.game.code,
        email: userEmail,
        group: this.g.user?.group || '',
        tag: config.game.tag || null,
        segment: this.g.user?.segment || '',
        type: config.game.code === 'quiz' ? 'QUIZ' : 'GAME',
        formationId: formationId,
        client: challengeUserClient,
      }

      // Calculate totalPointsAwarded before saving (for training context)
      if (timeMultiplier !== null && config.game.pointsWin && config.game.challengeWon === true) {
        const basePoints = +config.game.pointsWin
        const awardedPoints = Math.round(basePoints * timeMultiplier)

        let extraPoints = 0
        if (config.game.scriptPointsConfig) {
          if (config.game.scriptPointsConfig.forParticipating) {
            extraPoints += Math.round(config.game.scriptPointsConfig.forParticipating.points * timeMultiplier)
          }
          if (config.game.scriptPointsConfig.bySegmentParticipation) {
            extraPoints += Math.round(config.game.scriptPointsConfig.bySegmentParticipation.points * timeMultiplier)
          }
          if (config.game.scriptPointsConfig.hitAll && config.game.correctAnswers === config.game.questions?.length) {
            extraPoints += Math.round(config.game.scriptPointsConfig.hitAll.points * timeMultiplier)
          }
        }

        gameResult.totalPointsAwarded = awardedPoints + extraPoints
      }

      // Build effective user for points recording (participant email has priority)
      const effectiveUser = userEmail && userEmail !== (this.g.user?.email || '')
        ? { ...(this.g.user || {}), email: userEmail, group: this.g.user?.group || '', segment: this.g.user?.segment || '' }
        : (this.g.user || { email: userEmail, group: '', segment: '' })

      if (config.game.scenario === 'play-daily-points-once') {
        const today = await getServerTime()
        const timestamps = use<TimestampsService>(TimestampsService)
        const playedToday = config.userGames?.some((ug: any) => {
          const ugDate = timestamps.toDate(ug.serverDate || ug.createdAt)
          return ugDate instanceof Date && !isNaN(ugDate.getTime()) && isSameDay(ugDate, today)
        })

        if (playedToday) {
          config.game.pointsAwarded = false
          this.saveChallengeAnswer(answerBase, gameResult, timeToFinish, false, null, config.game)
        } else {
          config.game.pointsAwarded = !!config.game.challengeWon
          if (config.game.challengeWon) {
            const prizes = await this.givePrizesExtra(config.game)
            gameResult.prizes = prizes
          }
          this.saveChallengeAnswer(answerBase, gameResult, timeToFinish, false, null, config.game).then(() => {
            if (answerBase.challengeWon) {
              this.createUserTransaction(effectiveUser, config.game, timeToFinish, timeMultiplier)
            }
          })
        }
      } else if (config.game.scenario === 'play-multi-points-never' || (['play-once-points-once', 'play-multi-points-once'].includes(config.game.scenario) && config.userGames)) {
        this.saveChallengeAnswer(answerBase, gameResult, timeToFinish, false, null, config.game)
      } else {
        if (config.game.challengeWon && (!config.userGames || !config.userGames.length)) {
          const prizes = await this.givePrizesExtra(config.game)
          gameResult.prizes = prizes
        }
        this.saveChallengeAnswer(answerBase, gameResult, timeToFinish, true, timeMultiplier, config.game).then(() => {
          if (answerBase.challengeWon || config.game.challengeLost) {
            this.createUserTransaction(effectiveUser, config.game, timeToFinish, timeMultiplier)
          }
        })
      }

      return { win: answerBase.challengeWon, data: gameResult }
    } catch (err) {
      console.error('[saveGame] Unexpected error:', err)
      // Return data based on config.game for compatibility with legacy callers
      // that access result.data.prizes, result.data without null-check
      return { win: false, data: config.game || {}, error: err }
    } finally {
      // Clear sessionStorage after processing (always, even in case of error)
      sessionStorage.removeItem('challengeUserClient')
    }
  }

  createUserTransaction(user: any, game: any, timeToFinish: number, timeMultiplier: number | null = null): number {
    if (game.code === 'quiz') game.description = `[${game.name}] ${+game.pointsWin} pts por responder ao quiz`
    else game.description = `[${game.name}] ${+game.pointsWin} pts por vencer o desafio`

    // Apply multiplier if it exists (training context)
    const basePoints = +game.pointsWin
    const awardedPoints = timeMultiplier !== null ? Math.round(basePoints * timeMultiplier) : basePoints

    const baseTr: ChallengeTransaction = {
      challengeId: game.id,
      description: game.description,
      email: user.email,
      orderPoints: awardedPoints,
      segment: user.segment || '',
      tag: game.tag || null,
      type: game.code === 'quiz' ? 'QUIZ' : 'GAME',
    }

    if (!game.public && game.pointsWin && +game.pointsWin !== 0) {
      if (game.applyPointsTo === 'segment') this.applyPointsToSegment(user, baseTr, timeToFinish).then()
      else this.applyPointsToUser(user, baseTr, timeToFinish).then().catch()
    }

    return awardedPoints
  }

  async giveBadges(game: any): Promise<any[]> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const badges = await this.fss.list('badges', { where: [['active', '==', true], ['condition.gameId', '==', game.id]] }).pipe(first()).toPromise()
    const badgesWon = []

    if (badges && badges.length) {
      for (const badge of badges) {
        const hasBadge = this.g.user.badges && this.g.user.badges.find(b => b.id === badge.id)
        if (!hasBadge && game[badge.condition.script]) {
          badge.notified = true
          badge.receivedAt = new Date().toISOString()
          badgesWon.push(badge)
          if (!this.g.user.badges) this.g.user.badges = []
          this.fss.update('users', this.g.user.id, { badges: [...this.g.user.badges, badge] }).then()
        }
      }
    }

    return badgesWon
  }

  private async givePrizesExtra(game: any): Promise<any[]> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const prizes = []

    if (game.prizesExtra && game.prizesExtra.length) {
      for (const prize of game.prizesExtra) {
        const prizesWon = await this.fss.list('challenges', { where: [['challengeId', '==', game.id], ['prizes', 'array-contains', prize.uniq]] }).pipe(first()).toPromise()

        if (prizesWon.length < prize.limit) {
          prizes.push(prize.uniq)

          await this.sendEmailToManager(game, prize)
        }
      }
    }
    // console.log(prizes)
    return prizes
  }

  private sendEmailToManager(game: any, prize: any) {
    const emailSrv: EmailService = use<EmailService>(EmailService)
    const _program = {
      name: this.g.program.name,
      sender: this.g.program.sender,
      email: this.g.program.email,
      url: this.g.program.url,
      subject: `${this.g.program.name} | Prémio ${prize.value} atribuido a um utilizador por ganhar um jogo`,
      html: `
      Olá Gestor,
      <br><br>
      O utilizador <strong>${this.g.user.displayName}</strong> com o email <strong>${this.g.user.email}</strong> ganhou o jogo <strong>${game.name}</strong> e tem direito ao seguinte prémio <strong>${prize.value}</strong>.
      <br>
      Por favor envie o prémio ao participante antes que ele reclame.
      <br><br>
      Obrigado,
      `,
      imgHeader: this.g.program.images['emailHeader'],
      imgFooter: this.g.program.images['emailFooter'],
      templateId: this.g.program.emails['generic'],
      supportMobile: this.g.program.support?.contact || EmailService.DEFAULT_SUPPORT_MOBILE
    }

    const _item = {
      email: this.g.program.manager,
    }

    console.log(_item, _program)
    this.subs.add(
      emailSrv.sendOne(_item, _program).subscribe()
    )
  }

}
